import cn from 'classnames';
import { MPREmbeddedApi } from 'MPREmbedded';
import { FC, useEffect, useRef, useState } from 'react';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import { debug } from '@/shared/lib';

import { useCheckReportSignature } from '@/entities/reports';

import { MPRAddToReport } from '@/features/MPR';
import { useUploadAssetContext } from '@/features/uploadAsset';

import styles from './AddToReport.module.scss';

type AddToReportProps = {
  mprEmbedded: MPREmbeddedApi;
  toothID: string;
  className?: string;
};

export const AddToReport: FC<AddToReportProps> = (props) => {
  const { mprEmbedded, toothID, className } = props;

  const [attachTarget, setAttachTarget] = useState<Element | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const pendingRef = useRef(false);
  const { checkReportSignature } = useCheckReportSignature();
  const { startUploadAsset } = useUploadAssetContext();

  const onAddToReport = () => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    setIsLoading(true);
    const finish = () => {
      pendingRef.current = false;
      setIsLoading(false);
    };
    checkReportSignature({
      toothID,
      onCancel: finish,
      onSignatureChecked: async () => {
        try {
          const blob = await mprEmbedded.makeActiveViewportScreenshot();
          if (!blob) {
            throw new Error('Failed to capture the MPR pane');
          }
          const file = new File([blob], `${Date.now()}.png`, {
            type: 'image/png',
          });
          if (localStorage.getItem('DEBUG_DONT_UPLOAD') === 'true') {
            window.open(URL.createObjectURL(file), '_blank');
            return;
          }
          await startUploadAsset(
            {
              files: [file],
              assetType: AssetType.AssetType_Report_Uploaded_MPRPlane,
              toothID,
            },
            true,
          );
        } catch (error) {
          debug('[MPR] Failed to add a pane screenshot', error);
        } finally {
          finish();
        }
      },
    });
  };

  useEffect(() => {
    const unsubscribe = mprEmbedded.activeViewportChanged.on(
      ({ layoutElement }) => {
        setAttachTarget(layoutElement);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [mprEmbedded]);

  return (
    <MPRAddToReport
      attachTarget={attachTarget}
      isLoading={isLoading}
      className={cn(styles.self, className)}
      onAddToReport={onAddToReport}
    />
  );
};
